import React, { useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import type { FormValues } from '../EditCustomerWizard';

// List of major South African banks
const southAfricanBanks = [
  { name: 'ABSA Bank', code: '632005' },
  { name: 'Capitec Bank', code: '470010' },
  { name: 'First National Bank (FNB)', code: '250655' },
  { name: 'Nedbank', code: '198765' },
  { name: 'Standard Bank', code: '051001' },
  { name: 'African Bank', code: '430000' },
  { name: 'Bidvest Bank', code: '462005' },
  { name: 'Discovery Bank', code: '679000' },
  { name: 'Investec', code: '580105' },
  { name: 'TymeBank', code: '678910' },
  { name: 'Bank Zero', code: '888000' }
];

// Map of bank name abbreviations to full names for dropdown matching
const bankNameMapping: Record<string, string> = {
  'FNB': 'First National Bank (FNB)',
  'ABSA': 'ABSA Bank',
  'CAPITEC': 'Capitec Bank',
  'NEDBANK': 'Nedbank',
  'STANDARD BANK': 'Standard Bank', 
  'STANDARD': 'Standard Bank',
  'AFRICAN BANK': 'African Bank',
  'BIDVEST': 'Bidvest Bank',
  'DISCOVERY': 'Discovery Bank',
  'INVESTEC': 'Investec',
  'TYME': 'TymeBank',
  'BANK ZERO': 'Bank Zero'
};

// Function to normalize bank name
const normalizeBankName = (bankName: string): string => {
  if (!bankName) return '';
  
  // First, check exact match
  const exactMatch = southAfricanBanks.find(
    bank => bank.name.toLowerCase() === bankName.toLowerCase()
  );
  if (exactMatch) return exactMatch.name;
  
  // Check for abbreviations
  const uppercaseName = bankName.toUpperCase();
  for (const [abbr, fullName] of Object.entries(bankNameMapping)) {
    if (uppercaseName.includes(abbr)) {
      return fullName;
    }
  }
  
  return bankName;
};

const accountTypeOptions = [
  {
    value: 'SAVINGS',
    label: 'Savings Account',
  },
  {
    value: 'CURRENT_CHEQUE',
    label: 'Current/Cheque Account',
  },
  {
    value: 'TRANSMISSION',
    label: 'Transmission Account',
  },
  {
    value: 'BUSINESS',
    label: 'Business Account',
  },
];

const BankDetailsStep: React.FC = () => {
  const { register, formState: { errors }, setValue, watch } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    name: 'bankDetails'
  });

  // Auto-fill branch code when bank is selected
  useEffect(() => {
    // For each field, setup a watcher for bank name changes
    fields.forEach((field, index) => {
      const bankName = watch(`bankDetails.${index}.bankName`);
      
      if (bankName) {
        // Normalize the bank name
        const normalizedName = normalizeBankName(bankName);
        if (normalizedName !== bankName) {
          setValue(`bankDetails.${index}.bankName`, normalizedName);
        }
        
        // Set the branch code based on the selected bank
        const bank = southAfricanBanks.find(bank => bank.name === normalizedName);
        if (bank) {
          setValue(`bankDetails.${index}.branchCode`, bank.code);
        }
      }
    });
  }, [fields, watch, setValue]);

  // Add a specific watch for bank name changes
  const bankNames = fields.map((_, index) => watch(`bankDetails.${index}.bankName`));
  useEffect(() => {
    fields.forEach((field, index) => {
      const bankName = bankNames[index];
      if (bankName && bankName !== 'Other') {
        const bank = southAfricanBanks.find(bank => bank.name === bankName);
        if (bank) {
          setValue(`bankDetails.${index}.branchCode`, bank.code);
        }
      }
    });
  }, [bankNames, fields, setValue]);

  const handleAddBankDetail = () => {
    append({
      accountHolderName: '',
      bankName: '',
      accountNumber: '',
      accountType: '',
      branchCode: '',
    });
  };

  // Handle bank name change
  const handleBankNameChange = (index: number, bankName: string) => {
    if (bankName && bankName !== 'Other') {
      const bank = southAfricanBanks.find(bank => bank.name === bankName);
      if (bank) {
        setValue(`bankDetails.${index}.branchCode`, bank.code);
      }
    }
  };

  return (
    <div className="form-section">
      <h6 className="form-section-title">Bank Account Information</h6>
      <p className="text-muted mb-4">
        Please provide bank account details. This information will be used for loan disbursement and repayment.
      </p>
      
      <div className="d-flex justify-content-end mb-4">
        <button 
          type="button" 
          className="btn btn-outline-primary"
          onClick={handleAddBankDetail}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Add Bank Account
        </button>
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Bank Account {index + 1}</h6>
            {fields.length > 1 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => remove(index)}
              >
                <i className="bi bi-trash me-2"></i>
                Remove
              </button>
            )}
          </div>
          
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor={`accountHolderName-${index}`}>Account Holder Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.bankDetails?.[index]?.accountHolderName ? 'is-invalid' : ''}`}
                    id={`accountHolderName-${index}`}
                    placeholder="Enter account holder's full name"
                    {...register(`bankDetails.${index}.accountHolderName`, { 
                      required: 'Account holder name is required' 
                    })}
                  />
                  {errors.bankDetails?.[index]?.accountHolderName && (
                    <div className="invalid-feedback">{errors.bankDetails[index]?.accountHolderName?.message}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor={`bankName-${index}`}>Bank Name</label>
                  <select
                    className={`form-select ${errors.bankDetails?.[index]?.bankName ? 'is-invalid' : ''}`}
                    id={`bankName-${index}`}
                    {...register(`bankDetails.${index}.bankName`, { 
                      required: 'Bank name is required',
                      onChange: (e) => handleBankNameChange(index, e.target.value)
                    })}
                  >
                    <option value="">Select your bank</option>
                    {southAfricanBanks.map(bank => (
                      <option key={bank.code} value={bank.name}>{bank.name}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {errors.bankDetails?.[index]?.bankName && (
                    <div className="invalid-feedback">{errors.bankDetails[index]?.bankName?.message}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor={`branchCode-${index}`}>Branch Code</label>
                  <input
                    type="text"
                    className={`form-control ${errors.bankDetails?.[index]?.branchCode ? 'is-invalid' : ''}`}
                    id={`branchCode-${index}`}
                    placeholder="Enter branch code"
                    {...register(`bankDetails.${index}.branchCode`, { 
                      required: 'Branch code is required',
                      pattern: {
                        value: /^[0-9]{6}$/,
                        message: 'Please enter a valid 6-digit branch code'
                      }
                    })}
                    readOnly={!!watch(`bankDetails.${index}.bankName`) && watch(`bankDetails.${index}.bankName`) !== 'Other'}
                  />
                  {errors.bankDetails?.[index]?.branchCode && (
                    <div className="invalid-feedback">{errors.bankDetails[index]?.branchCode?.message}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor={`accountNumber-${index}`}>Account Number</label>
                  <input
                    type="text"
                    className={`form-control ${errors.bankDetails?.[index]?.accountNumber ? 'is-invalid' : ''}`}
                    id={`accountNumber-${index}`}
                    placeholder="Enter account number"
                    {...register(`bankDetails.${index}.accountNumber`, { 
                      required: 'Account number is required',
                      pattern: {
                        value: /^[0-9]{6,16}$/,
                        message: 'Please enter a valid account number (6-16 digits)'
                      }
                    })}
                  />
                  {errors.bankDetails?.[index]?.accountNumber && (
                    <div className="invalid-feedback">{errors.bankDetails[index]?.accountNumber?.message}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor={`accountType-${index}`}>Account Type</label>
                  <select
                    className={`form-select ${errors.bankDetails?.[index]?.accountType ? 'is-invalid' : ''}`}
                    id={`accountType-${index}`}
                    {...register(`bankDetails.${index}.accountType`, { 
                      required: 'Account type is required' 
                    })}
                  >
                    <option value="">Select account type</option>
                    {accountTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.bankDetails?.[index]?.accountType && (
                    <div className="invalid-feedback">{errors.bankDetails[index]?.accountType?.message}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {fields.length === 0 && (
        <div className="text-center py-5 bg-light rounded">
          <i className="bi bi-bank fs-1 text-muted"></i>
          <p className="mt-3 text-muted">No bank accounts added yet</p>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={handleAddBankDetail}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add Bank Account
          </button>
        </div>
      )}
      
      <div className="alert alert-info mt-3">
        <div className="d-flex">
          <div className="me-3">
            <i className="bi bi-info-circle fs-4"></i>
          </div>
          <div>
            <p className="mb-1"><strong>Important:</strong> Please ensure that the account details provided are correct.</p>
            <ul className="mb-0">
              <li>The account must be in the customer's name</li>
              <li>The account must be active and able to receive funds</li>
              <li>Incorrect bank details may result in failed transactions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankDetailsStep; 